import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Layout } from '@/components/Layout';
import { QuoteReview } from '@/components/JobCostPlanning';
import { ProjectChangeSummary } from './ProjectChangeSummary';

export function ProjectRevisionHistory({ jobId, refreshKey, onChange, standalone = false }) {
  const [data, setData] = useState(null), [error, setError] = useState(''), [busy, setBusy] = useState(false);
  const load = async () => { try { setData(await api(`/jobs/${jobId}/revisions`)); setError(''); } catch (e) { setError(e.message); } };
  useEffect(() => { load(); }, [jobId, refreshKey]);
  useEffect(() => {
    const timer = setInterval(() => { if (document.visibilityState !== 'hidden') load(); }, 15000);
    return () => clearInterval(timer);
  }, [jobId]);
  const changed = async () => { await load(); onChange?.(); };
  const withdraw = async revision => {
    setBusy(true);
    try { await api(`/jobs/${jobId}/revisions/withdraw`, { method: 'POST', body: { revision_id: revision.id } }); await changed(); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  if (!data) return error ? <p role="alert">Could not load project updates: {error}</p> : null;
  if (!standalone && !data.items.length) return null;
  const pending = data.items.find(r => ['awaiting_contractor', 'awaiting_client'].includes(r.state));
  return <section className="avcp-card p-5 space-y-4 mb-6">
    <h2 className="text-xl font-semibold">{standalone ? `${data.title} — project updates` : 'Project updates'}</h2>
    {error && <p role="alert" className="text-rose-700">{error}</p>}
    {pending && <div className="rounded-xl border border-amber-400 bg-amber-50 p-4 space-y-3">
      <p className="text-sm">These changes are proposed. The current agreement remains in place until the contractor reconfirms and the client approves the quote.</p>
      {standalone ? <ProjectChangeSummary revision={pending} /> : <details><summary className="cursor-pointer font-medium">Review the proposed changes</summary><div className="mt-3"><ProjectChangeSummary revision={pending} /></div></details>}
      {data.audience === 'client' && <div className="flex flex-wrap gap-3">
        {standalone && <Link className="avcp-btn primary" to={`/app/jobs/${jobId}?edit=1`}>Edit proposed changes</Link>}
        <button className="avcp-btn ghost" disabled={busy} onClick={() => withdraw(pending)}>Withdraw proposed changes</button>
      </div>}
    </div>}
    {!data.items.length && <p className="text-sm">No changes have been republished yet.</p>}
    {data.items.filter(r => r.id !== pending?.id).map(revision => <details key={revision.id} className="border rounded-xl p-3">
      <summary className="cursor-pointer text-sm">Revision {revision.version} · {new Date(revision.created_at).toLocaleString()} · {revision.state === 'applied' ? 'Published' : revision.state}</summary>
      <div className="mt-3"><ProjectChangeSummary revision={revision} /></div>
    </details>)}
    {standalone && data.booking_id && ['client','contractor'].includes(data.audience) && <QuoteReview bookingId={data.booking_id} onChange={changed} />}
    {standalone && data.audience === 'client' && <Link className="avcp-btn ghost" to={`/app/jobs/${jobId}`}>Open project</Link>}
  </section>;
}
export function ProjectUpdates() {
  const { id } = useParams();
  return <Layout><ProjectRevisionHistory jobId={id} standalone /></Layout>;
}
